import { useAuthenticationStore } from '@/features/authentication/presentation/stores/useAuthenticationStore'
import getConnection from '@/utils/signalr'
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import eventBus from '@/utils/eventBus'
import type { HubConnection } from '@microsoft/signalr'

let sharedConnection: HubConnection | null = null
let sharedRetryTimeout: ReturnType<typeof setTimeout> | null = null
let isWatchingToken = false
let boundConnection: HubConnection | null = null

export const signalRConnectionState = ref<string>('Disconnected')

const updateConnectionState = (conn: HubConnection) => {
  signalRConnectionState.value = conn.state
}

const clearRetryTimeout = () => {
  if (sharedRetryTimeout) {
    clearTimeout(sharedRetryTimeout)
    sharedRetryTimeout = null
  }
}

const scheduleReconnect = (conn: HubConnection) => {
  if (sharedRetryTimeout || conn !== sharedConnection) return

  sharedRetryTimeout = setTimeout(() => {
    sharedRetryTimeout = null

    if (conn === sharedConnection && conn.state === 'Disconnected') {
      void startConnection(conn)
    }
  }, 5000)
}

const bindConnectionEvents = (conn: HubConnection) => {
  if (boundConnection === conn) return

  conn.onreconnecting(() => {
    updateConnectionState(conn)
  })

  conn.onreconnected(() => {
    updateConnectionState(conn)
    eventBus.emit('signalr-connected')
  })

  conn.onclose(() => {
    updateConnectionState(conn)
    scheduleReconnect(conn)
  })

  boundConnection = conn
}

async function startConnection(conn: HubConnection) {
  bindConnectionEvents(conn)
  clearRetryTimeout()
  updateConnectionState(conn)

  if (conn.state === 'Connected') {
    eventBus.emit('signalr-connected')
    return
  }

  if (conn.state !== 'Disconnected') {
    return
  }

  try {
    await conn.start()
    updateConnectionState(conn)
    eventBus.emit('signalr-connected')
  } catch {
    updateConnectionState(conn)
    scheduleReconnect(conn)
  }
}

const useSignalRConnection = () => {
  const authStore = useAuthenticationStore()
  const { authenticationData } = storeToRefs(authStore)

  // Initialize the connection if it doesn't exist yet
  if (!sharedConnection) {
    sharedConnection = getConnection(
      import.meta.env.VITE_MARKET_URL,
      authenticationData.value.accessToken ?? ''
    )
    void startConnection(sharedConnection)
  } else {
    updateConnectionState(sharedConnection)
  }

  if (!isWatchingToken) {
    watch(
      () => authenticationData.value.accessToken,
      async (newToken) => {
        if (sharedConnection && sharedConnection.state === 'Connected') {
          await sharedConnection.stop()
        }

        sharedConnection = getConnection(import.meta.env.VITE_MARKET_URL, newToken ?? '')
        updateConnectionState(sharedConnection)
        void startConnection(sharedConnection)

        eventBus.emit('signalr-instance-changed')
      }
    )
    isWatchingToken = true
  }

  return sharedConnection
}

export default useSignalRConnection
